"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import type { FUSportsLivescoreEvent, SearchEntityResult } from "fansunited-data-layer";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { cn } from "@/utilities/core/cn";
import type { MatchOddsMap } from "@/features/odds/types";
import type { LivescoreSport, LivescoreFilter } from "@/views/livescore/utils/livescore";
import { asCompetitor } from "@/views/livescore/utils/livescore";
import { MatchTvIndicator } from "@/features/tv-listings/frontend/MatchTvIndicator";
import type { ResolvedListings } from "@/features/tv-listings/utils/resolveMatchListings";
import { FootballMatchRow } from "./match-row/FootballMatchRow";
import { BasketballMatchRow } from "./match-row/BasketballMatchRow";
import { TennisMatchRow } from "./match-row/TennisMatchRow";
import { HockeyMatchRow } from "./match-row/HockeyMatchRow";
import { AmericanFootballMatchRow } from "./match-row/AmericanFootballMatchRow";
import { LivescoreOdds } from "./match-row/LivescoreOdds";
interface LivescoreMatchRowProps {
  event: FUSportsLivescoreEvent;
  entities: Record<string, SearchEntityResult>;
  sport: LivescoreSport;
  filter: LivescoreFilter;
  oddsMap: MatchOddsMap;
  isMobile: boolean;
  /** Pre-resolved TV listings for this match, from the by-date batch. Modal opens with
   *  this data instantly — no lazy fetch. Undefined → no broadcasters for this match. */
  tvListings?: ResolvedListings;
  /** When true, the TV slot is reserved even for rows without broadcasters so widths stay
   *  aligned across the view. Driven by the parent — set when any match in the day has TV. */
  reserveTvSlot?: boolean;
}

/** Sports that have a dedicated match detail page. */
const SPORTS_WITH_MATCH_PAGE = new Set<LivescoreSport>(["football", "basketball", "tennis", "hockey", "american-football"]);
export function LivescoreMatchRow(t0) {
  const $ = _c(62);
  const {
    event,
    entities,
    sport,
    filter,
    oddsMap,
    isMobile,
    tvListings,
    reserveTvSlot: t1
  } = t0;
  const reserveTvSlot = t1 === undefined ? false : t1;
  const labels = useLabels();
  const nav = useNavigation();
  const {
    hasOdds
  } = useTenantSettings();
  let t2;
  if ($[0] !== sport) {
    t2 = SPORTS_WITH_MATCH_PAGE.has(sport);
    $[0] = sport;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const hasMatchPage = t2;
  let t3;
  if ($[2] !== event.id || $[3] !== hasMatchPage || $[4] !== nav || $[5] !== sport) {
    t3 = hasMatchPage ? nav.sportEvent(sport, event.id) : undefined;
    $[2] = event.id;
    $[3] = hasMatchPage;
    $[4] = nav;
    $[5] = sport;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const href = t3;
  const firstBookmaker = hasOdds ? oddsMap.get(event.id)?.[0] : undefined;
  const showOddsColumn = hasOdds && filter !== "finished";
  const isLive = event.status?.type === "LIVE";
  const liveBorderClass = isLive ? sport === "football" ? "border-l-2 border-l-green-500" : "border-l-2 border-l-orange-500" : "";
  asCompetitor(entities, event.competitorOne.id);
  asCompetitor(entities, event.competitorTwo.id);
  let t4;
  if ($[7] !== entities || $[8] !== event || $[9] !== sport) {
    t4 = sport === "basketball" && <BasketballMatchRow event={event} entities={entities} />;
    $[7] = entities;
    $[8] = event;
    $[9] = sport;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== entities || $[12] !== event || $[13] !== sport) {
    t5 = sport === "tennis" && <TennisMatchRow event={event} entities={entities} />;
    $[11] = entities;
    $[12] = event;
    $[13] = sport;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== entities || $[16] !== event || $[17] !== sport) {
    t6 = sport === "football" && <FootballMatchRow event={event} entities={entities} />;
    $[15] = entities;
    $[16] = event;
    $[17] = sport;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== entities || $[20] !== event || $[21] !== sport) {
    t7 = sport === "hockey" && <HockeyMatchRow event={event} entities={entities} />;
    $[19] = entities;
    $[20] = event;
    $[21] = sport;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== entities || $[24] !== event || $[25] !== sport) {
    t8 = sport === "american-football" && <AmericanFootballMatchRow event={event} entities={entities} />;
    $[23] = entities;
    $[24] = event;
    $[25] = sport;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t4 || $[28] !== t5 || $[29] !== t6 || $[30] !== t7 || $[31] !== t8) {
    t9 = <>{t4}{t5}{t6}{t7}{t8}</>;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const matchContent = t9;
  let t10;
  if ($[33] !== liveBorderClass) {
    t10 = cn("hover:bg-muted/40 transition-colors", liveBorderClass);
    $[33] = liveBorderClass;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== event.id || $[36] !== labels || $[37] !== reserveTvSlot || $[38] !== tvListings) {
    t11 = reserveTvSlot && <div className="shrink-0 w-5 flex items-center justify-center mr-1">{tvListings && <MatchTvIndicator matchNativeId={event.id} labels={labels} hasBroadcasters={true} resolved={tvListings} />}</div>;
    $[35] = event.id;
    $[36] = labels;
    $[37] = reserveTvSlot;
    $[38] = tvListings;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== href || $[41] !== matchContent) {
    t12 = href ? <Link href={href} prefetch={false} className="flex-1 min-w-0 block cursor-pointer">{matchContent}</Link> : <div className="flex-1 min-w-0">{matchContent}</div>;
    $[40] = href;
    $[41] = matchContent;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== event.id || $[44] !== event.startTime || $[45] !== firstBookmaker || $[46] !== showOddsColumn || $[47] !== sport) {
    t13 = showOddsColumn && <div className={cn("hidden md:flex shrink-0 ml-2 self-center", sport === "football" ? "md:w-85 md:max-w-85" : "md:w-72")}>{firstBookmaker && <LivescoreOdds bookmakerOdds={firstBookmaker} isMobile={false} matchId={event.id} kickoffAt={event.startTime ?? undefined} />}</div>;
    $[43] = event.id;
    $[44] = event.startTime;
    $[45] = firstBookmaker;
    $[46] = showOddsColumn;
    $[47] = sport;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  let t14;
  if ($[49] !== t11 || $[50] !== t12 || $[51] !== t13) {
    t14 = <div className="flex items-stretch px-2">{t11}{t12}{t13}</div>;
    $[49] = t11;
    $[50] = t12;
    $[51] = t13;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  let t15;
  if ($[53] !== event.id || $[54] !== event.startTime || $[55] !== firstBookmaker || $[56] !== isMobile) {
    t15 = firstBookmaker && <div className="md:hidden pb-2 px-2"><LivescoreOdds bookmakerOdds={firstBookmaker} isMobile={isMobile} matchId={event.id} kickoffAt={event.startTime ?? undefined} /></div>;
    $[53] = event.id;
    $[54] = event.startTime;
    $[55] = firstBookmaker;
    $[56] = isMobile;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  let t16;
  if ($[58] !== t10 || $[59] !== t14 || $[60] !== t15) {
    t16 = <div className={t10}>{t14}{t15}</div>;
    $[58] = t10;
    $[59] = t14;
    $[60] = t15;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  return t16;
}